import { onUnmounted } from "vue";
import utilService from "@/services/util.service";
import storageService from "@/services/storage.service";
import { trackError, trackEvent } from "@/helpers/tracking.helpers";
import { SocketEvents, TrackingEvents } from "@/helpers/enum";
import SocketConnectionBuilder from "@/helpers/socket/socket-connection.builder";
import { SocketStateManager } from "@/helpers/socket/socket-state.singleton";
import { StockSocketTransferDTO } from "@/helpers/models/stock/stock_socket_transfer.dto";
import { PRICE_SOCKET_LOGGING_ENABLED } from "@/helpers/constants";
import { useNatsStreamer, scheduleNatsSync as scheduleNatsSyncInternal } from "./nats_streamer.composable";
import {
  PRICE_SOURCE,
  stripLivePriceFields,
  withNatsPrice,
} from "@/helpers/live-price.helpers";

const state = SocketStateManager.getInstance();
const natsStreamer = useNatsStreamer();

let lastNatsSyncKey = "";

const trackSocketError = (error: any, context: string, metadata?: Record<string, any>) => {
  trackError(error, {
    context: `Socket:${context}`,
    socketConnected: state.socket?.connected,
    ...metadata,
  });
};

const trackSocketEvent = (eventName: string, metadata?: Record<string, any>) => {
  trackEvent(eventName, {
    socketConnected: state.socket?.connected,
    ...metadata,
  });
};

const applyPriceToStocks = (incomingStockId: string, price: number) => {
  const stockIdKey = String(incomingStockId).replace("-USD", "_CRYPTO");
  const [symbol, exchange] = stockIdKey.toUpperCase().split("_");
  const isCrypto = exchange === "CRYPTO";

  const idsFromWatchList = state.stockIds
    .map((s) => s.stockId)
    .filter((id): id is string => !!id)
    .filter((id) => {
      if (isCrypto) {
        return id === stockIdKey || id.replace("-USD", "_CRYPTO") === stockIdKey;
      }
      const [sym] = id.toUpperCase().split("_");
      return sym === symbol && !id.endsWith("_CRYPTO");
    });

  const idsFromStore = isCrypto
    ? state.stocks
        .filter((x: any) => x.stockId === stockIdKey)
        .map((x: any) => x.stockId)
    : state.stocks
        .filter(
          (x: any) =>
            x.symbol?.toUpperCase() === symbol && x.exchange !== "CRYPTO"
        )
        .map((x: any) => x.stockId);

  const stockIdsToUpdate = Array.from(
    new Set([...idsFromWatchList, ...idsFromStore, stockIdKey])
  ).filter(
    (id) =>
      !!state.stocksData[id] ||
      state.stockIds.some((s) => s.stockId === id)
  );

  stockIdsToUpdate.forEach((stockId) => {
    const base = state.stocksData[stockId] || { stockId };
    const previousClose = base.previousClose;
    const priceChange =
      previousClose && previousClose !== 0
        ? (((price - previousClose) / previousClose) * 100).toFixed(2)
        : null;

    state.stocksData[stockId] = withNatsPrice(base, price, priceChange);
  });
};

const handleNatsPriceUpdate = (subjectStockId: string, data: any) => {
  try {
    const price = data?.price;
    if (typeof price !== "number") return;

    const incomingStockId =
      (typeof data?.tvId === "string" && data.tvId) || subjectStockId;
    applyPriceToStocks(incomingStockId, price);
  } catch (error) {
    trackSocketError(error, "NatsPriceUpdateProcessing", {
      subjectStockId,
      data,
    });
  }
};

const getDesiredNatsIds = (): string[] =>
  state.stockIds.map((s) => s.stockId).filter((id): id is string => !!id);

const syncNatsIfNeeded = () => {
  const desiredIds = getDesiredNatsIds();
  if (!desiredIds.length) return;

  const syncKey = desiredIds.slice().sort().join("|");
  if (syncKey === lastNatsSyncKey) return;
  lastNatsSyncKey = syncKey;

  const token = storageService.getToken();
  if (!token) return;

  natsStreamer
    .connectToNats(token)
    .then(() => {
      if (!natsStreamer.isConnected.value) return;
      return natsStreamer.syncSubscriptions(desiredIds, handleNatsPriceUpdate);
    })
    .catch((err) => {
      console.error("[NATS pricing] connect/subscribe error:", err);
    });
};

/** Debounced — call when new symbols are registered on the page. */
export const scheduleNatsSync = () => {
  scheduleNatsSyncInternal(getDesiredNatsIds, handleNatsPriceUpdate);
};

/** Register symbol for NATS live price only (no getstocksdata). */
export const registerSymbolForLivePrice = (
  stockId: string,
  metadata?: Record<string, any>
) => {
  if (!stockId) return;

  const index = state.stockIds.findIndex((x) => x.stockId === stockId);
  if (index === -1) {
    state.stockIds.push(new StockSocketTransferDTO(stockId, false));
  }

  const existing = state.stocksData[stockId] || { stockId };
  const previousClose =
    metadata?.previousClose ?? existing.previousClose ?? metadata?.past;

  state.stocksData[stockId] = {
    ...existing,
    stockId,
    previousClose,
    symbol: metadata?.symbol ?? existing.symbol,
    name: metadata?.name ?? existing.name,
    ...(metadata?.price != null && existing[PRICE_SOURCE] !== "nats"
      ? {
          price: metadata.price,
          changesPercentage:
            metadata.changesPercentage ?? metadata.chgpct ?? existing.changesPercentage,
          [PRICE_SOURCE]: metadata[PRICE_SOURCE] ?? "facts",
        }
      : {}),
  };

  scheduleNatsSync();
};

export const usePricingSocket = () => {
  const loggingEnabled = PRICE_SOCKET_LOGGING_ENABLED;
  const socketBuilder = SocketConnectionBuilder.create();

  const setupSocketListeners = () => {
    if (!state.socket) return;

    if (loggingEnabled) console.log("Pricing: Socket Listener Placed");
    const onPriceUpdate = (data: any) => handlePriceUpdate(data);

    socketBuilder
      .on(SocketEvents.priceupdate, onPriceUpdate)
      .on(SocketEvents.disconnect, () => {
        if (loggingEnabled) console.log("Pricing: Socket disconnected");
      });

    state.addCleanup(() => {
      if (loggingEnabled) console.log("Pricing: Socket CleanUp");
    });
  };

  const connectToSocket = () => {
    if (loggingEnabled) console.log("Pricing: Socket Connection Initiated..");

    if (state.socket?.connected) {
      if (loggingEnabled) console.log("Pricing: Socket already connected");
      return;
    }

    if (!state.socket || !state.socket.connected) {
      if (loggingEnabled) console.log("Pricing: Initializing new socket connection");

      state.socket = socketBuilder
        .on(SocketEvents.connect, () => {
          if (loggingEnabled) console.log("Pricing: Socket connected successfully");
          trackSocketEvent(TrackingEvents.socketConnected, {
            url: utilService.getLiveHostURL(),
          });
          setupSocketListeners();
          syncNatsIfNeeded();
        })
        .on(SocketEvents.disconnect, (reason) => {
          if (loggingEnabled) console.log("Pricing: Socket disconnected:", reason);
          trackSocketEvent(TrackingEvents.socketDisconnected, { reason });
        })
        .on(SocketEvents.connect_error, (error) => {
          if (loggingEnabled) console.error("Connection error:", error);
          trackSocketError(error, TrackingEvents.socketConnectionError);
        })
        .on(SocketEvents.error, (error) => {
          if (loggingEnabled) console.error("Socket error:", error);
          trackSocketError(error, TrackingEvents.socketError);
        })
        .connect();
    }
  };

  const disconnectFromSocket = () => {
    try {
      lastNatsSyncKey = "";
      natsStreamer.disconnect().catch(() => {});
      state.socket?.removeAllListeners();
      state.socket?.close();
      state.socket = null;
      console.log("Pricing: Socket Disconnected..");
      trackSocketEvent(TrackingEvents.socketManualDisconnect);
    } catch (error) {
      trackSocketError(error, "DisconnectionError");
    }
  };

  const handlePriceUpdate = (data: any) => {
    try {
      const obj = JSON.parse(data);
      if (typeof obj.price !== "number" || !obj.stockId) return;
      applyPriceToStocks(obj.stockId, obj.price);
    } catch (error) {
      trackSocketError(error, TrackingEvents.priceUpdateProcessingError, {});
    }
  };

  const clearStockIds = () => {
    // state.stockIds = [];
  };

  onUnmounted(() => {
    if (loggingEnabled) {
      console.log("Pricing Socket: Unmounting");
    }
    state.cleanup();
  });

  return {
    connectToSocket,
    disconnectFromSocket,
    clearStockIds,
  };
};
